import { getByID, subscribeEntityByID } from '@adornis/baseql/operations/mongo.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { routingState } from '@adornis/router/client/routing-state.js';
import '@ils/osces/client/ils-osce-type.js';
import { Slot } from '@ils/slots/db/slot.js';
import { html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { state } from 'lit/decorators/state.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { ILSUser } from '../../db/ils-user.js';
import '../components/video/ils-video-chat.js';
import type { ILSVideoChat } from '../components/video/ils-video-chat.js';

@customElement('ils-spectate')
export class ILSSpectate extends ChemistryLitElement {
  override styles() {
    return [
      {
        ':host': {
          flex: '1',
          display: 'flex',
          flexDirection: 'column',
        },
      },
    ];
  }
  @property({ attribute: 'slot-id' }) currentSlotID = new RXController(this, '');
  @state() public currentSlot = new RXController(
    this,
    this.currentSlotID.observable.pipe(
      switchMap(slot =>
        slot
          ? subscribeEntityByID(Slot, [slot])({
              _class: 1,
              _id: 1,
              tester: 1,
              candidateID: 1,
              startTime: 1,
              roomID: 1,
              endTime: 1,
              inspectors: 1,
              osce: {
                _class: 1,
                _id: 1,
                title: 1,
                titleTrainee: 1,
                taskType: 1,
                osceType: 1,
                conversationType: 1,
                enableVideo: 1,
              },
            })
          : of([]),
      ),
    ),
  );

  @state() users = new RXController(
    this,
    this.currentSlot.observable.pipe(
      switchMap(slot => {
        if (slot?.length) {
          const arr = [slot[0]!.candidateID];
          if (slot[0]!.tester) arr.push(slot[0]!.tester);
          return getByID(
            ILSUser,
            arr,
          )({ _id: 1, ecommerce: { firstName: 1, lastName: 1, fullName: 1 }, username: 1 }).then(users =>
            users.sort((a, b) => arr.indexOf(a._id) - arr.indexOf(b._id)),
          );
        }
        return of([] as ILSUser[]);
      }),
    ),
  );

  @state() private readonly enableMic = new RXController(this, false);
  private readonly videoChatElem = createRef<ILSVideoChat>();

  override render() {
    if (!this.currentSlot.value?.length || !this.users.value?.length) return nothing;
    const [slot] = this.currentSlot.value;
    if (!slot) return nothing;
    const [candidate, tester] = this.users.value;
    return html`
      <x-flex space="lg" flex ${css({ width: '100%', minHeight: '0', height: '100%' })}>
        <x-flex
          horizontal
          crossaxis-center
          space="md"
          ${css({ padding: `${this.spacing.lg} ${this.spacing.lg} 0 ${this.spacing.lg}` })}
        >
          <x-button
            mode="icon"
            icon="arrow_back"
            href="/start/scheduling?${routingState.searchParams.getValue().toString()}"
          ></x-button>
          <x-h1> <ils-osce-type .osce=${slot.osce}></ils-osce-type> Beobachten: ${slot.osce?.titleTrainee} </x-h1>
          <x-text> Prüfling: ${candidate?.ecommerce?.fullName ?? candidate?.username} </x-text>
          <x-text> Schauspieler: ${tester?.ecommerce?.fullName ?? tester?.username} </x-text>
          <x-flex horizontal crossaxis-center space="xs">
            <x-icon
              ${css({
                fontSize: this.spacing.md,
                color: this.enableMic.value ? this.colors.accent : this.colors.neutral.shade(400),
              })}
            >
              ${this.enableMic.value ? 'mic' : 'mic_off'}
            </x-icon>
            <x-button
              @click=${() => {
                this.enableMic.next(!this.enableMic.value);
                this.videoChatElem.value?.toggleMicrophone();
                this.videoChatElem.value?.toggleInvisible();
              }}
              mode=${this.enableMic.value ? 'outline' : 'action'}
              ${css({ marginLeft: 'auto' })}
            >
              Mikrofon ${this.enableMic.value ? 'abschalten' : 'anschalten'}
            </x-button>
          </x-flex>
        </x-flex>
        <ils-video-chat
          flex
          ${ref(this.videoChatElem)}
          ${css({ padding: this.spacing.md })}
          ?disableMicrophone=${!this.enableMic.value}
          disableVideo
          incognito
          room-id=${slot.roomID}
        >
        </ils-video-chat>
      </x-flex>
    `;
  }
}
